import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { DecimalPipe } from "@angular/common";

import { Bloque, DocEjecucion, DocPuntaje, DocTarea } from "../../shared/model/iso42001/homologacion-modelo.model";
import { BloqueEvidenciaComponent } from "../bloque-evidencia/bloque-evidencia.component";

type Pestana = "esperado" | "obtenido" | "repeticiones";

/**
 * «Qué se evaluó»: una tarea en acordeón con tres pestañas.
 *   Lo esperado   la descripción, los rubros, el umbral y los bloques
 *                 «esperado» tal como la suite los describe.
 *   Lo obtenido   capacidad ± σ, rubros, costo y latencia.
 *   Repeticiones  cada una con duración, costo, tokens, turnos, entrega y
 *                 cada rubro con sus NOTAS (dicen el porqué) y su evidencia.
 *
 * Todo lo del archivo va por interpolación o por app-bloque-evidencia.
 */
@Component({
  selector: "app-tarea-detalle",
  standalone: true,
  imports: [DecimalPipe, BloqueEvidenciaComponent],
  templateUrl: "./tarea-detalle.component.html",
  styleUrls: ["./tarea-detalle.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TareaDetalleComponent {
  @Input() tarea!: DocTarea;
  /** id -> nombre de los rubros de la rúbrica. */
  @Input() nombresRubro: Record<string, string> = {};

  abierta = false;
  pestana: Pestana = "esperado";

  get esperado(): Bloque[] {
    return Array.isArray(this.tarea?.esperado) ? this.tarea.esperado : [];
  }

  get ejecuciones(): DocEjecucion[] {
    // Solo objetos: un null en el arreglo tiraría la pantalla al leer e.repeticion.
    return Array.isArray(this.tarea?.ejecuciones)
      ? this.tarea.ejecuciones.filter((e) => !!e && typeof e === "object")
      : [];
  }

  get rubros(): string[] {
    return Array.isArray(this.tarea?.rubros) ? this.tarea.rubros : [];
  }

  get rubrosObtenidos(): { id: string; valor: number | null }[] {
    const r = this.tarea?.obtenido?.rubros;
    if (!r || typeof r !== "object") {
      return [];
    }
    return Object.keys(r).map((id) => ({ id, valor: this.numero(r[id]) }));
  }

  puntajes(e: DocEjecucion): DocPuntaje[] {
    return Array.isArray(e?.puntajes) ? e.puntajes.filter((p) => !!p && typeof p === "object") : [];
  }

  notas(p: DocPuntaje): string[] {
    return Array.isArray(p?.notas) ? p.notas : [];
  }

  evidencia(p: DocPuntaje): Bloque[] {
    return Array.isArray(p?.evidencia) ? p.evidencia : [];
  }

  nombreRubro(id: string, nombre?: string): string {
    return nombre || this.nombresRubro[id] || id;
  }

  numero(v: unknown): number | null {
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  }

  alterna(): void {
    this.abierta = !this.abierta;
  }

  elige(p: Pestana): void {
    this.pestana = p;
  }

  /** Los tokens del harness: entrada + salida (sin caché) es lo que cuenta en tokens_totales. */
  tokens(e: DocEjecucion): string {
    const t = e?.tokens;
    if (!t) {
      return "—";
    }
    const partes: string[] = [];
    if (this.numero(t.entrada) !== null) {
      partes.push(`${t.entrada} entrada`);
    }
    if (this.numero(t.salida) !== null) {
      partes.push(`${t.salida} salida`);
    }
    if (this.numero(t.cache_lectura) !== null) {
      partes.push(`${t.cache_lectura} caché leída`);
    }
    if (this.numero(t.cache_escritura) !== null) {
      partes.push(`${t.cache_escritura} caché escrita`);
    }
    return partes.length ? partes.join(" · ") : "—";
  }

  archivos(e: DocEjecucion): string {
    const a = e?.archivos;
    if (!a) {
      return "—";
    }
    return `${a.modificados ?? 0} modificados · ${a.creados ?? 0} creados · ${a.borrados ?? 0} borrados`;
  }
}
